import { FC, MouseEvent, PropsWithChildren, RefObject, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { Box, IconButton, List, ListItem, Stack } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import Divider from '@mui/material/Divider'
import MuiDrawer from '@mui/material/Drawer'
import { CSSObject, styled, Theme, useTheme } from '@mui/material/styles'
import camelcase from 'camelcase'
import { useLocalStorage, useOnClickOutside } from 'usehooks-ts'

import { IPSLogo, IPSNameLogo2025 } from '@/assets/icons'
import {
  APP_BAR_HEIGHT,
  DRAWER_WIDTH_CLOSED_PX,
  DRAWER_WIDTH_OPEN_PX,
  IPS_AQUA,
  NAV_BACKGROUND,
  NAV_DIVIDER,
  NAV_DRAWER_RADIUS_PX,
  NEUTRAL_GREY_50,
} from '@/common/constants'
import { LocalStorageKey } from '@/common/utils/localStorage'
import { ICON_MAP } from '@/components/componentLibrary/Icon/Icons'
import { NavDrawerContext, navDrawerTransition } from '@/components/layouts/CurbsNav/navDrawer'
import { BottomSection, PageItem } from '@/components/layouts/CurbsNav/types'
import { useCurbPages } from '@/components/layouts/CurbsNav/useCurbPages'
import { useMobilityPages } from '@/components/layouts/CurbsNav/useMobilityPages'
import CurbsNavDrawerItem from '@/components/layouts/CurbsNavDrawerItem'
import CurbsNavFlyout, { NAV_FLYOUT_ID } from '@/components/layouts/CurbsNavFlyout'
import CurbsNavSectionHeader, {
  FLYOUT_TRIGGER_ATTRIBUTE,
} from '@/components/layouts/CurbsNavSectionHeader'
import CurbsNavDrawerSwitchSection from '@/components/layouts/CurbsNavSwitchSection'
import TrialExpirationWarning from '@/components/TrialExpirationWarning'
import ImpersonatedUserBannerMUI from '@/modules/authentication/ImpersonatedUserBannerMUI'
import { publicBucket } from '@/modules/initialization/config'
import { useCurrentProduct, useCurrentRegion } from '@/modules/urlRouting/hooks'
import { SEGMENTS } from '@/modules/urlRouting/paths'
import ProductSelectorMUI from '@/modules/urlRouting/ProductSelectorMUI'
import RegionSelectorMUI from '@/modules/urlRouting/RegionSelectorMUI'

// The toggle straddles the drawer's right edge, near the bottom but clear of
// the Switch section.
const TOGGLE_SIZE_PX = 32

// A nav item's stable handle. It also names the item in its test id.
const pageId = (page: PageItem): string => page.testId ?? camelcase(page.title)
const TOGGLE_BOTTOM_PX = 120

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH_OPEN_PX,
  transition: navDrawerTransition(theme, true, 'width'),
  overflowX: 'hidden',
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: navDrawerTransition(theme, false, 'width'),
  overflowX: 'hidden',
  width: DRAWER_WIDTH_CLOSED_PX,
})

const Drawer = styled(MuiDrawer, { shouldForwardProp: prop => prop !== 'open' })(({ theme }) => ({
  width: DRAWER_WIDTH_OPEN_PX,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  position: 'absolute',
  '& .MuiDrawer-paper': {
    backgroundColor: NAV_BACKGROUND,
    color: '#fff',
    borderRight: 'none',
    borderRadius: `0 ${NAV_DRAWER_RADIUS_PX}px 0 0`,
  },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}))

const CurbsNav: FC<PropsWithChildren> = ({ children }) => {
  // The toggle button is the only thing that opens or closes the drawer, and the
  // choice outlives the session — the drawer pushes the page rather than
  // covering it, so there is nothing to collapse away from.
  const [open, setOpen] = useLocalStorage<boolean>(LocalStorageKey.NAV_DRAWER_OPEN, false)
  // Two flags rather than one: the toggle straddles the drawer's edge, so
  // moving onto it fires the drawer's mouseleave and would flicker it away.
  const [hoveringDrawer, setHoveringDrawer] = useState<boolean>(false)
  const [hoveringToggle, setHoveringToggle] = useState<boolean>(false)
  const toggleRevealed = hoveringDrawer || hoveringToggle

  const theme = useTheme()

  const width = open ? DRAWER_WIDTH_OPEN_PX : DRAWER_WIDTH_CLOSED_PX

  /*
    The page steps to its new offset once, after the drawer has finished moving,
    rather than riding the animation.

    A transition on the content's margin resizes every ResizeObserver inside it
    once a frame, and MUI's charts answer each one with a forced sync layout and
    a full re-render of the SVG — enough to reduce the animation to a few
    visible steps. Landing the step on the animation's last frame is not much
    better: that one re-render is expensive enough on a chart-heavy page to
    stutter the frames it shares. So the drawer gets the main thread to itself,
    and the page catches up when it is done.
  */
  const [contentOffset, setContentOffset] = useState<number>(width)

  useEffect(() => {
    if (contentOffset === width) return undefined

    const handle = window.setTimeout(
      () => setContentOffset(width),
      open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen
    )
    return () => window.clearTimeout(handle)
  }, [contentOffset, width, open, theme.transitions.duration])

  // mapbox-gl only resizes off window resize events — it holds no ResizeObserver
  // — so every map would keep the canvas it had before the page narrowed. One
  // synthetic event once the page has settled catches all of them at once.
  const settledOffset = useRef<number>(contentOffset)
  useEffect(() => {
    if (settledOffset.current === contentOffset) return
    settledOffset.current = contentOffset
    window.dispatchEvent(new Event('resize'))
  }, [contentOffset])

  const {
    data: { regionId, text: regionName, logoFilename },
  } = useCurrentRegion()

  const currentProduct = useCurrentProduct()
  const isMobility = currentProduct === SEGMENTS.MOBILITY

  // Both lists are built every render: hooks cannot be called conditionally.
  const curbPages = useCurbPages()
  const mobilityPages = useMobilityPages()
  const productPages: PageItem[] = isMobility ? mobilityPages : curbPages

  /*
    A group's sub-pages open in a flyout beside the drawer. It floats over the
    page, so the page keeps its width, and it opens the same way whether the
    drawer is open or collapsed — a group click never changes the drawer.

    The group id is kept after the flyout closes, so its sub-pages stay in place
    while it slides out. The group is looked up from the live page list, so the
    current sub-page stays correct as the user navigates.
  */
  const [flyoutOpen, setFlyoutOpen] = useState<boolean>(false)
  const [flyoutGroupId, setFlyoutGroupId] = useState<string>()
  const flyoutGroup = productPages.find(page => page.children && pageId(page) === flyoutGroupId)
  const flyoutRef = useRef<HTMLElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  // The header that opened the flyout, to give focus back to on Escape.
  const flyoutTriggerRef = useRef<HTMLElement | null>(null)

  const toggleFlyout = (groupId: string, event: MouseEvent<HTMLElement>) => {
    flyoutTriggerRef.current = event.currentTarget
    if (flyoutOpen && flyoutGroupId === groupId) {
      setFlyoutOpen(false)
      return
    }
    setFlyoutGroupId(groupId)
    setFlyoutOpen(true)
  }

  // Following a link closes the flyout, the same as the old Mobility drawer.
  const { pathname } = useLocation()
  useEffect(() => setFlyoutOpen(false), [pathname])

  /*
    A click anywhere else closes it. Group headers handle their own clicks, to
    close or to switch groups. The drawer toggle does not close it either: the
    flyout follows the drawer's edge, so the user can resize the drawer with it
    open.
  */
  useOnClickOutside([flyoutRef, toggleRef] as RefObject<HTMLElement>[], event => {
    if (!flyoutOpen) return
    if (event.target instanceof Element && event.target.closest(`[${FLYOUT_TRIGGER_ATTRIBUTE}]`)) {
      return
    }
    setFlyoutOpen(false)
  })

  useEffect(() => {
    if (!flyoutOpen) return undefined

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setFlyoutOpen(false)
      flyoutTriggerRef.current?.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [flyoutOpen])

  const navDrawerSwitch: BottomSection = {
    title: 'Switch',
    testId: 'switch',
    icon: <ICON_MAP.sync />,
    items: [
      {
        title: 'Product',
        component: <ProductSelectorMUI />,
      },
      {
        title: 'Region',
        component: <RegionSelectorMUI />,
      },
    ],
  }

  return (
    <NavDrawerContext.Provider value={{ open, contentOffset }}>
      <CssBaseline />
      {/*
        The drawer's rounded top-right corner cuts away the paper and reveals the
        page background behind it. Left alone that shows white against the grey
        app bar, so this strip paints the corner's backdrop in the bar's color.

        It runs the full height of the app bar and the full width of the page's
        offset, rather than just the corner and the drawer's own width, so it
        also covers the band the drawer uncovers on its way closed — the page
        holds its offset until the animation ends, and body white against the
        grey bar reads as a hole. Below the bar the page background is that same
        white, so there is nothing to cover.
      */}
      <Box
        aria-hidden
        sx={theme => ({
          position: 'fixed',
          top: 0,
          left: 0,
          width: `${contentOffset}px`,
          height: APP_BAR_HEIGHT,
          backgroundColor: NEUTRAL_GREY_50,
          zIndex: theme.zIndex.drawer - 1,
        })}
      />
      {/*
        A `nav` landmark, so screen reader users can jump to the navigation, and
        the drawer and its toggle are inside a landmark (axe `region`). It holds
        only the absolutely positioned drawer and the fixed toggle, so it takes
        no space and does not change the layout.
      */}
      <Box component="nav" aria-label="Main">
        <Drawer
          variant="permanent"
          open={open}
          onMouseOver={() => setHoveringDrawer(true)}
          onMouseLeave={() => setHoveringDrawer(false)}
        >
          <Stack justifyContent="space-between" height="100vh">
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent={open ? 'flex-start' : 'center'}
                sx={{ minHeight: APP_BAR_HEIGHT, px: open ? 3.5 : 2 }}
              >
                <Box sx={{ height: '40px', display: 'flex', alignItems: 'center' }}>
                  {open ? <IPSNameLogo2025 height={32} width="auto" /> : <IPSLogo />}
                </Box>
              </Stack>
              <Box>
                {/*
                One list holding both plain items and groups. A group's sub-pages
                are not in this list: they open in the flyout beside the drawer.
              */}
                <List sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {productPages.map(page =>
                    page.children ? (
                      <ListItem key={page.title} disablePadding sx={{ display: 'block' }}>
                        <CurbsNavSectionHeader
                          flyoutTrigger
                          drawerOpen={open}
                          icon={page.icon}
                          title={page.title}
                          testId={pageId(page)}
                          disabled={page.disabled}
                          active={page.isActive}
                          expanded={flyoutOpen && flyoutGroupId === pageId(page)}
                          controls={NAV_FLYOUT_ID}
                          onClick={event => toggleFlyout(pageId(page), event)}
                        />
                      </ListItem>
                    ) : (
                      <CurbsNavDrawerItem
                        drawerOpen={open}
                        key={page.title}
                        icon={page.icon}
                        testId={pageId(page)}
                        title={page.title}
                        disabled={page.disabled}
                        isActive={page.isActive}
                        to={page.link}
                      />
                    )
                  )}
                </List>
              </Box>
            </Box>
            <Box display="flex" flexDirection="column" alignItems="center">
              <ImpersonatedUserBannerMUI open={open} />
              {/* Mobility Manager only: the curb products have no trial banner. */}
              {isMobility && open && <TrialExpirationWarning />}
            </Box>
            <Stack>
              {logoFilename && !open && (
                <Box width="100%" display="flex" flexDirection="column" alignItems="center">
                  <img
                    // Shown only in the collapsed drawer, where no text names the region.
                    alt={`${regionName} logo`}
                    style={{ height: '45px' }}
                    src={`${publicBucket}/regions/${regionId}/${logoFilename}`}
                    crossOrigin="anonymous"
                  />
                </Box>
              )}
              <Box key={navDrawerSwitch.title}>
                <Divider sx={{ borderColor: NAV_DIVIDER }} />
                <CurbsNavDrawerSwitchSection
                  drawerOpen={open}
                  onExpandDrawer={() => setOpen(true)}
                  section={navDrawerSwitch}
                />
              </Box>
            </Stack>
          </Stack>
        </Drawer>
        {/*
        Sits outside the Drawer paper, which clips its overflow — the button
        straddles the edge rather than sitting inside it.
      */}
        <IconButton
          ref={toggleRef}
          onClick={() => setOpen(current => !current)}
          onMouseOver={() => setHoveringToggle(true)}
          onMouseLeave={() => setHoveringToggle(false)}
          aria-label={open ? 'Collapse navigation' : 'Expand navigation'}
          aria-expanded={open}
          // The aria-label flips with the state, so tests need a stable handle.
          data-testid="curb-nav-toggle"
          sx={theme => ({
            position: 'fixed',
            bottom: `${TOGGLE_BOTTOM_PX}px`,
            left: `${width}px`,
            transform: 'translateX(-50%)',
            zIndex: theme.zIndex.drawer + 1,
            width: `${TOGGLE_SIZE_PX}px`,
            height: `${TOGGLE_SIZE_PX}px`,
            p: 0,
            color: IPS_AQUA,
            backgroundColor: 'common.white',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[2],
            '&:hover': { backgroundColor: 'common.white' },
            // Revealed on hover, but kept mounted so it stays keyboard-reachable.
            opacity: toggleRevealed ? 1 : 0,
            '&:focus-visible': { opacity: 1 },
            transition: [
              theme.transitions.create('opacity', {
                duration: theme.transitions.duration.shorter,
              }),
              navDrawerTransition(theme, open, 'left'),
            ].join(', '),
          })}
        >
          <ICON_MAP.chevronRight
            sx={{ fontSize: 20, transform: open ? 'rotate(180deg)' : 'none' }}
          />
        </IconButton>
      </Box>
      {/*
        Given the drawer's width rather than `contentOffset`, so it follows the
        drawer's edge while it animates. It floats over the page, so the page
        offset does not change.
      */}
      <CurbsNavFlyout
        open={flyoutOpen}
        group={flyoutGroup}
        drawerWidth={width}
        drawerOpen={open}
        panelRef={flyoutRef}
      />
      {/*
        The sticky app bar rides this scroller, so an overscroll bounce drags it
        down and exposes the white container behind it. No bounce, no gap.
      */}
      <Box
        sx={{
          ml: `${contentOffset}px`,
          height: '100vh',
          overflow: 'auto',
          overscrollBehavior: 'none',
          /*
            Keeps every z-index in the page inside the page, so no part of the
            page can paint over the nav chrome (drawer, toggle, sub-page flyout).
            Without it, a page's own MUI Drawer — the Policies filter sidebar,
            for one — gets `theme.zIndex.drawer` and covers the flyout, which
            sits one level below the nav drawer.

            `isolation` and not a `z-index` or `transform`: it changes stacking
            only. It does not make this box the containing block of the fixed
            map overlays, which place themselves against the viewport.
          */
          isolation: 'isolate',
          /*
            Mobility Manager's floating sidebars and its reporting dashboards are
            `position: absolute` and need this box as their containing block,
            or they resolve against the viewport and land under the nav drawer.
            The old Shelf layout did the same on `.shelf-content`.

            Deliberately `position` and not a `transform`: a transform would also
            capture `position: fixed` children, and the map overlays are fixed and
            already offset by `contentOffset` themselves. Scoped to Mobility so
            the curb products keep the plain box they shipped with.
          */
          ...(isMobility ? { position: 'relative' } : {}),
        }}
      >
        {children}
      </Box>
    </NavDrawerContext.Provider>
  )
}

export default CurbsNav
